/**
 * SA retail banks with universal branch codes (matches services/disbursement/branchCodeResolver.js).
 * Use exact labels in CSV bank_name column and in manual-entry dropdowns.
 */

export const SA_BANK_OPTIONS = [
  'ABSA',
  'FNB',
  'Nedbank',
  'Standard Bank',
  'Capitec',
  'African Bank',
  'Discovery Bank',
  'Investec',
  'TymeBank',
  'Bidvest Bank',
  'Sasfin',
  'Bank Zero',
  'Old Mutual',
] as const;

export type SaBankName = (typeof SA_BANK_OPTIONS)[number];

/** Lowercase aliases → canonical dropdown / CSV label */
const BANK_ALIASES: Record<string, SaBankName> = {
  absa: 'ABSA',
  fnb: 'FNB',
  'first national bank': 'FNB',
  'first national': 'FNB',
  nedbank: 'Nedbank',
  'standard bank': 'Standard Bank',
  capitec: 'Capitec',
  'african bank': 'African Bank',
  'discovery bank': 'Discovery Bank',
  discovery: 'Discovery Bank',
  investec: 'Investec',
  tymebank: 'TymeBank',
  'tyme bank': 'TymeBank',
  'bidvest bank': 'Bidvest Bank',
  bidvest: 'Bidvest Bank',
  sasfin: 'Sasfin',
  'bank zero': 'Bank Zero',
  'old mutual': 'Old Mutual',
};

function normaliseKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Map free-text bank_name (CSV) to a canonical option, or null if unknown.
 */
export function resolveCanonicalBankName(raw: string | null | undefined): SaBankName | null {
  if (!raw?.trim()) return null;
  const key = normaliseKey(raw);
  if (BANK_ALIASES[key]) return BANK_ALIASES[key];
  const exact = SA_BANK_OPTIONS.find((b) => normaliseKey(b) === key);
  if (exact) return exact;
  for (const bank of SA_BANK_OPTIONS) {
    const label = normaliseKey(bank);
    if (key.includes(label) || label.includes(key)) return bank;
  }
  return null;
}

export function formatBankListForHelp(): string {
  return SA_BANK_OPTIONS.join(', ');
}
